// One screen for dragging faces onto a picture: the picture as big as fits, with a handle on each eye, brow and mouth
// to drag, and a tray underneath. The tray has a chip per person (with adding and removing people when the cast isn't
// fixed), the size / tilt / parts tools, zoom and test-the-mouth, a face style choice when given one, and a strip of
// frames along the bottom when given frames.
// Used for a character's face (one face, with a face style), a picture's faces (many people) and each frame of an
// animation. The faces are controlled: every change goes out through `onFaces`, and the caller decides what to save.
import { useRef, useState, type PointerEvent } from "react";
import { FRAME_H, FRAME_W, WHOLE, pictureFit, viewRect } from "../../engine/camera";
import type { DrawFrame } from "../../engine/frame";
import { useLibrary } from "../../library/store";
import { faceStyles } from "../../library/faceSets";
import type { Camera, Picture } from "../../model/episode";
import type { FaceSpots, Viseme } from "../../model/face";
import { findAsset } from "../../model/library";
import { spreadFaces, type PaintSource, type PlacedFace } from "../../model/timeline";
import { FrameSvg } from "../../render/FrameSvg";
import { Avatar, FitFrame, Icon, useIdleClock } from "../common";
import { FaceTools, TALK, scaleFace, shift, tiltFace, type Part } from "./faceTools";
import { FacePreview } from "../library/FacePreview";
import { CastPicker } from "./CastPicker";
import "./placer.css";

// What the caller draws: the picture with the faces on it, seen through `camera`. `talk`: one person's mouth is open.
export type PlacerDraw = (o: { clock: number; camera: Camera; talk?: { char: string; viseme: Viseme } }) => DrawFrame;

// The strip of frames along the bottom, for an animation. The caller owns which frame is showing.
export type PlacerFrames = {
  srcs: string[]; marked: boolean[]; index: number; onIndex: (i: number) => void; // marked: frames with a hand fix
  watching: boolean; onWatch: (on: boolean) => void;
  label: string; // "Frame 2 of 8: placed by hand"
  onUndo?: () => void; // takes off the hand fix on this frame, when it has one
  note: string; // what to do, when it has none
};

export function Placer({ picture: pic, faces, onFaces, draw, hint, fixedCast, solo, refs = [], styles, frames }: {
  picture: Picture; faces: PlacedFace[]; onFaces: (faces: PlacedFace[]) => void; draw: PlacerDraw;
  hint?: string; // "" for none
  fixedCast?: boolean; // who has a face can't change here
  solo?: boolean; // one face only, so there are no person chips
  refs?: PaintSource[]; // what the picture was painted from: those characters come first when adding someone
  styles?: { value: string; onChange: (id: string) => void }; // the face styles to choose from
  frames?: PlacerFrames;
}) {
  const lib = useLibrary(); // custom face styles appear when they are made
  const clock = useIdleClock();
  const [who, setWho] = useState<string | undefined>(() => faces[0]?.char);
  const [part, setPart] = useState<Part>("face");
  const [zoom, setZoom] = useState(false);
  const [talk, setTalk] = useState(false);
  const [held, setHeld] = useState<Camera | null>(null); // the camera stays put while dragging, even zoomed on a face
  const [adding, setAdding] = useState(false); // the character picker is open
  const frameEl = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; y: number } | null>(null);

  const name = (id: string | undefined) => findAsset(lib, id ?? "")?.name ?? "them";
  const fit = pictureFit(pic);
  const face = faces.find(f => f.char === who)?.face;
  const camera: Camera = held ?? (zoom && face
    ? { cx: (fit.x + face.eyes.x * fit.s) / FRAME_W, cy: (fit.y + ((face.eyes.y + face.mouth.y) / 2) * fit.s) / FRAME_H, zoom: Math.min(6, Math.max(1.5, FRAME_W / (face.eyes.w * fit.s * 3.2))) }
    : WHOLE);
  const view = viewRect(camera);
  const frame = draw({ clock, camera, talk: talk && who !== undefined ? { char: who, viseme: TALK[Math.floor(clock * 8) % TALK.length]! } : undefined });

  const setFace = (f: (x: FaceSpots & { tilt?: number }) => FaceSpots) => onFaces(faces.map(x => (x.char === who ? { ...x, face: f(x.face) } : x)));
  const select = (id: string) => { setWho(id); setTalk(false); setPart("face"); };

  // From the screen to the picture's pixels, through the camera.
  const toPic = (e: PointerEvent) => {
    const r = frameEl.current!.getBoundingClientRect();
    const sx = view.x + ((e.clientX - r.left) / r.width) * view.w, sy = view.y + ((e.clientY - r.top) / r.height) * view.h;
    return { x: (sx - fit.x) / fit.s, y: (sy - fit.y) / fit.s };
  };
  // The face nearest the pointer, if the pointer is on it (within a face's width of its middle).
  const hit = (p: { x: number; y: number }) => {
    let best: { char: string; d: number } | undefined;
    for (const f of faces) {
      const cx = (f.face.eyes.x + f.face.mouth.x) / 2, cy = (f.face.eyes.y + f.face.mouth.y) / 2;
      const d = Math.hypot(p.x - cx, p.y - cy);
      if (d <= Math.max(f.face.eyes.w * 1.6, 40) && (!best || d < best.d)) best = { char: f.char, d };
    }
    return best?.char;
  };
  function down(e: PointerEvent<HTMLDivElement>) {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const p = toPic(e), h = hit(p);
    if (h !== undefined && h !== who) select(h);
    if (h === undefined && who === undefined) return;
    drag.current = p;
    setHeld(camera);
  }
  function move(e: PointerEvent<HTMLDivElement>) {
    const d = drag.current;
    if (!d || !face) return;
    const q = toPic(e);
    const dx = Math.max(-face.eyes.x, Math.min(pic.w - face.eyes.x, q.x - d.x)), dy = Math.max(-face.eyes.y, Math.min(pic.h - face.eyes.y, q.y - d.y));
    drag.current = q;
    setFace(f => (part === "face" ? { ...f, eyes: shift(f.eyes, dx, dy), brows: shift(f.brows, dx, dy), mouth: shift(f.mouth, dx, dy) } : { ...f, [part]: shift(f[part], dx, dy) }));
  }
  const up = () => { drag.current = null; setHeld(null); };
  const handle = view.w * 0.022;

  function add(id: string) {
    onFaces([...faces, spreadFaces([...faces.map(f => f.char), id], pic).pop()!]);
    select(id);
  }
  // Takes someone off the picture; if they were selected, the first person left is.
  function drop(id: string) {
    const rest = faces.filter(f => f.char !== id);
    onFaces(rest);
    if (who === id) { setWho(rest[0]?.char); setTalk(false); }
  }

  return (
    <div className="placer faces">
      <FitFrame frameRef={frameEl} className="editing" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
        <FrameSvg id="placer" boil={false} frame={frame} />
        {face && (
          <button className={`zoom-btn${zoom ? " on" : ""}`} onPointerDown={e => e.stopPropagation()} onClick={() => setZoom(!zoom)}
            aria-label={zoom ? "Zoom out to the whole picture" : "Zoom in"} title={zoom ? "Zoom out" : "Zoom in"}><Icon name={zoom ? "zoom-out" : "zoom-in"} size={22} /></button>
        )}
        <svg className="face-marks" viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`} preserveAspectRatio="none">
          {faces.map(f => (["eyes", "brows", "mouth"] as const).map(k => (
            <circle key={f.char + k} cx={fit.x + f.face[k].x * fit.s} cy={fit.y + f.face[k].y * fit.s} r={handle}
              className={f.char === who && (part === "face" || part === k) ? "on" : ""} opacity={f.char === who ? 1 : 0.6} />
          )))}
        </svg>
      </FitFrame>

      <div className="placer-tray">
        {hint !== "" && (
          <p className="hint">{hint ?? (faces.length ? "Tap a face to select it and drag to move it. Use the sliders to change its size or tilt." : "Add the characters who are on screen with this picture. Faces show from the start of the picture to its end.")}</p>
        )}
        {styles && (
          <div className="face-style-row" role="radiogroup" aria-label="Face style">
            {faceStyles().map(s => (
              <button key={s.id} role="radio" aria-checked={styles.value === s.id} className={`face-style${styles.value === s.id ? " on" : ""}`} disabled={!s.ready} onClick={() => styles.onChange(s.id)}>
                <FacePreview style={s.id} /><span>{s.ready ? s.name : `${s.name} (making…)`}</span>
              </button>
            ))}
          </div>
        )}
        {!solo && (faces.length > 0 || !fixedCast) && (
          <div className="placer-who" role="radiogroup" aria-label="Faces on this picture">
            {faces.map(f => (
              <button key={f.char} role="radio" aria-checked={who === f.char} className={`who-chip${who === f.char ? " on" : ""}`} onClick={() => select(f.char)}>
                <Avatar assetId={f.char} size={32} />{name(f.char)}
              </button>
            ))}
            {!fixedCast && <button className="who-chip" onClick={() => setAdding(true)}><Icon name="plus" size={18} />Add</button>}
          </div>
        )}
        {face && (
          <FaceTools face={face} part={part} onPart={setPart} maxW={pic.w * 0.5} talk={talk} onTalk={setTalk}
            onSize={w => setFace(f => (part === "face" ? scaleFace(f, w / f.eyes.w) : { ...f, [part]: { ...f[part], w } }))}
            onTilt={deg => setFace(f => tiltFace(f, deg))} onTurn={turn => setFace(({ turn: _old, ...f }) => (turn ? { ...f, turn } : f))} />
        )}
        {who !== undefined && !fixedCast && !solo && face && (
          <button className="btn small danger" onClick={() => drop(who)}><Icon name="trash" size={16} />Take {name(who)} off this picture</button>
        )}
        {frames && (
          <div className="frame-nav">
            <div className="frame-strip">
              {frames.srcs.map((src, j) => (
                <button key={j} className={j === frames.index ? "on" : ""} onClick={() => frames.onIndex(j)} aria-label={`Frame ${j + 1}`}>
                  <img src={src} alt="" loading="lazy" />
                  {frames.marked[j] ? <span className="mark" /> : null}
                </button>
              ))}
            </div>
            <div className="frame-row">
              <button className="icon-btn" disabled={frames.index === 0} onClick={() => frames.onIndex(frames.index - 1)} aria-label="Previous frame"><Icon name="back" /></button>
              <button className="icon-btn" onClick={() => frames.onWatch(!frames.watching)} aria-label={frames.watching ? "Stop" : "Watch"}><Icon name={frames.watching ? "pause" : "play"} /></button>
              <span>{frames.label}</span>
              <button className="icon-btn" disabled={frames.index === frames.srcs.length - 1} onClick={() => frames.onIndex(frames.index + 1)} aria-label="Next frame"><Icon name="chevron" /></button>
            </div>
            {frames.onUndo
              ? <button className="btn small" onClick={frames.onUndo}><Icon name="trash" size={18} /> Undo the fix on this frame</button>
              : <p className="hint">{frames.note}</p>}
          </div>
        )}
      </div>
      {adding && <CastPicker faces={faces} refs={refs} onAdd={add} onDrop={drop} onClose={() => setAdding(false)} />}
    </div>
  );
}
